// Product slider: native swipe on touch, click-and-drag with momentum on desktop,
// a draggable progress scrubber with a live "01 / 12" count, and a square "Drag"
// cursor that follows the pointer over the products (fine pointers only).
class SSlider extends HTMLElement {
  #controller;
  #frame = 0;
  #momentum = 0;

  connectedCallback() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const track = this.querySelector('[data-slider-track]');
    if (!track) return;

    const progress = this.querySelector('[data-slider-progress]');
    const thumb = this.querySelector('[data-slider-thumb]');
    const current = this.querySelector('[data-slider-current]');
    const total = this.querySelector('[data-slider-total]');
    const cursor = this.querySelector('[data-slider-cursor]');
    const items = [...track.children];
    const pad = (n) => String(n).padStart(2, '0');
    const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isRtl = () => getComputedStyle(track).direction === 'rtl';
    const maxScroll = () => Math.max(track.scrollWidth - track.clientWidth, 0);
    const position = () => Math.abs(track.scrollLeft);
    const setPosition = (value) => {
      const clamped = Math.min(Math.max(value, 0), maxScroll());
      track.scrollLeft = isRtl() ? -clamped : clamped;
    };

    if (total) total.textContent = pad(items.length);

    // Progress thumb + count.
    const update = () => {
      const max = maxScroll();
      const ratio = max > 0 ? position() / max : 0;
      const visible = track.clientWidth / Math.max(track.scrollWidth, 1);
      if (thumb && progress) {
        const width = Math.max(visible, 0.08) * 100;
        thumb.style.width = `${width}%`;
        thumb.style.insetInlineStart = `${ratio * (100 - width)}%`;
        progress.hidden = max <= 0;
      }
      if (current && items.length) {
        const step = items[0].offsetWidth + (parseFloat(getComputedStyle(track).columnGap) || 0);
        const perView = Math.max(1, Math.round(track.clientWidth / step));
        const index = ratio >= 0.999 ? items.length : Math.min(items.length, Math.round(position() / step) + perView);
        current.textContent = pad(index);
      }
    };
    const queueUpdate = () => {
      cancelAnimationFrame(this.#frame);
      this.#frame = requestAnimationFrame(update);
    };
    track.addEventListener('scroll', queueUpdate, { passive: true, signal });
    window.addEventListener('resize', queueUpdate, { passive: true, signal });
    update();

    // Mouse drag with momentum (touch keeps native swipe).
    let dragging = false;
    let moved = 0;
    let startX = 0;
    let startPos = 0;
    let lastX = 0;
    let lastTime = 0;
    let velocity = 0;
    const dir = () => (isRtl() ? -1 : 1);

    track.addEventListener(
      'pointerdown',
      (event) => {
        if (event.pointerType !== 'mouse' || event.button !== 0) return;
        cancelAnimationFrame(this.#momentum);
        dragging = true;
        moved = 0;
        startX = lastX = event.clientX;
        startPos = position();
        lastTime = performance.now();
        velocity = 0;
        track.style.scrollSnapType = 'none';
        cursor?.style.setProperty('scale', '0.8');
      },
      { signal },
    );

    window.addEventListener(
      'pointermove',
      (event) => {
        if (!dragging) return;
        const delta = event.clientX - startX;
        moved = Math.max(moved, Math.abs(delta));
        setPosition(startPos - delta * dir());
        const now = performance.now();
        velocity = ((event.clientX - lastX) / Math.max(now - lastTime, 1)) * 16;
        lastX = event.clientX;
        lastTime = now;
      },
      { signal },
    );

    const endDrag = () => {
      if (!dragging) return;
      dragging = false;
      cursor?.style.setProperty('scale', '1');
      const release = () => {
        track.style.scrollSnapType = '';
      };
      if (reduceMotion() || Math.abs(velocity) < 0.5) {
        release();
        return;
      }
      const glide = () => {
        velocity *= 0.92;
        setPosition(position() - velocity * dir());
        if (Math.abs(velocity) > 0.5) this.#momentum = requestAnimationFrame(glide);
        else release();
      };
      this.#momentum = requestAnimationFrame(glide);
    };
    window.addEventListener('pointerup', endDrag, { signal });
    window.addEventListener('pointercancel', endDrag, { signal });

    // A drag must not open the product underneath.
    track.addEventListener(
      'click',
      (event) => {
        if (moved > 6) {
          event.preventDefault();
          event.stopPropagation();
        }
        moved = 0;
      },
      { capture: true, signal },
    );
    track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });

    // Scrubber: press or drag on the bar to move through the products.
    if (progress) {
      let scrubbing = false;
      const scrubTo = (clientX) => {
        const rect = progress.getBoundingClientRect();
        let ratio = (clientX - rect.left) / Math.max(rect.width, 1);
        if (isRtl()) ratio = 1 - ratio;
        track.style.scrollSnapType = 'none';
        setPosition(Math.min(Math.max(ratio, 0), 1) * maxScroll());
      };
      progress.addEventListener(
        'pointerdown',
        (event) => {
          scrubbing = true;
          progress.setPointerCapture(event.pointerId);
          scrubTo(event.clientX);
        },
        { signal },
      );
      progress.addEventListener('pointermove', (event) => scrubbing && scrubTo(event.clientX), { signal });
      const endScrub = () => {
        if (!scrubbing) return;
        scrubbing = false;
        track.style.scrollSnapType = '';
      };
      progress.addEventListener('pointerup', endScrub, { signal });
      progress.addEventListener('pointercancel', endScrub, { signal });
    }

    // Square "Drag" cursor on devices with a precise pointer.
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    if (cursor && finePointer.matches) {
      cursor.classList.remove('hidden');
      cursor.classList.add('flex');
      track.style.cursor = 'none';
      const move = (event) => {
        cursor.style.translate = `${event.clientX - cursor.offsetWidth / 2}px ${event.clientY - cursor.offsetHeight / 2}px`;
      };
      track.addEventListener(
        'pointerenter',
        (event) => {
          move(event);
          cursor.style.opacity = '1';
        },
        { signal },
      );
      track.addEventListener('pointermove', move, { signal });
      track.addEventListener(
        'pointerleave',
        () => {
          if (!dragging) cursor.style.opacity = '0';
        },
        { signal },
      );
      window.addEventListener('pointerup', (event) => !track.matches(':hover') && (cursor.style.opacity = '0'), { signal });
      window.addEventListener('pointermove', (event) => dragging && move(event), { signal });
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    cancelAnimationFrame(this.#momentum);
  }
}

if (!customElements.get('s-slider')) customElements.define('s-slider', SSlider);
